import { MLSGroupManager } from "@agentvault/crypto";

/**
 * A KeyPackage bundle: the public package plus the private-key material needed
 * to join a group the package was added to. This is exactly what
 * ``MLSGroupManager.generateKeyPackage`` returns.
 */
export type KpBundle = Awaited<ReturnType<MLSGroupManager["generateKeyPackage"]>>;

/**
 * Try each candidate KeyPackage bundle against an incoming Welcome until one
 * decrypts. MLS Welcomes are HPKE-encrypted to exactly one KeyPackage's init
 * key; with the backend consuming published KeyPackages newest-first (#508), the agent
 * cannot predict which of its published KPs a given room's creator was served.
 * So it trial-decrypts the Welcome against every pending bundle it holds.
 *
 * A per-candidate failure means "wrong KP", not a broken Welcome — only when
 * ALL candidates fail does the join genuinely fail (returns null). On success
 * the freshly-joined ``MLSGroupManager`` and the bundle that worked are
 * returned so the caller can persist the group state and drop the consumed
 * bundle from its pool.
 *
 * Duplicate bundles (same public package) are attempted once.
 */
export async function joinWithFirstMatchingKp(
  welcomeBytes: Uint8Array,
  candidates: Array<KpBundle | null | undefined>,
): Promise<{ mgr: MLSGroupManager; kp: KpBundle } | null> {
  const seen = new Set<string>();
  for (const kp of candidates) {
    if (!kp) continue;
    const id = Buffer.from(
      MLSGroupManager.serializeKeyPackage(kp.publicPackage),
    ).toString("base64");
    if (seen.has(id)) continue;
    seen.add(id);

    // A fresh manager per attempt — a failed joinFromWelcome leaves its
    // manager unusable, so a clean one is required for the next candidate.
    const mgr = new MLSGroupManager();
    try {
      await mgr.joinFromWelcome(welcomeBytes, kp);
      return { mgr, kp };
    } catch {
      // Wrong KP for this Welcome (or otherwise unjoinable with it) — the
      // Welcome was encrypted to a different published KeyPackage. Try next.
    }
  }
  return null;
}
